// src/components/CalendarDaysCard.jsx
// 大学の授業カレンダー（教務）【2026-09-28】
//
//   学年暦（PDF）の休業日・一部休講・振替を、1日1行の文字で貼り付けて登録する。
//     2026-11-23 休業 勤労感謝の日
//     2026-12-29〜2027-01-05 休業 冬期休業
//     2026-05-13 一部休講 5講時以降 降誕会
//     2026-05-30 振替 5講時以降 水曜 水曜日7回目分
//     2026-10-12 授業実施 スポーツの日
//   「下見」で読み取り結果と書き換わる日を見てから「登録」する。同じ日は上書き。
//   【2026-09-28】月のカレンダーから日を選んで入れる画面を足した（ふだんはこちら）。
//     日をクリックで選ぶ（もう一度で外す）・Shift+クリックで範囲。選んだ日に種類を付けて登録。
//     貼り付けは「まとめて貼り付け」に畳んだ（学年暦から一気に入れるとき用）。
//   【2026-09-28】曜日でまとめて選ぶボタン（例: 日曜を年度ぶん全部選んで「休業」）。
//     範囲は「この月」か「年度全体」。設定済みの日（授業実施・振替など）は選ばない。
//   サーバー: /api/calendar-days（calendarController）
import React, { useEffect, useState } from 'react';
import Notice from './Notice';
import { confirmDialog } from './ConfirmDialog';

const auth = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token'), 'Content-Type': 'application/json' });
const DAY = ['日', '月', '火', '水', '木', '金', '土'];
const KIND = {
  holiday:   { label: '休業',     color: '#b91c1c', bg: '#fee2e2' },
  partial:   { label: '一部休講', color: '#b45309', bg: '#fef3c7' },
  swap:      { label: '振替',     color: '#7c3aed', bg: '#ede9fe' },
  class_day: { label: '授業実施', color: '#15803d', bg: '#dcfce7' },
  no_auto:   { label: '自動開始なし', color: '#0369a1', bg: '#e0f2fe' },   // サマーセッションなど【2026-09-28】
};
const thisYear = () => { const d = new Date(); return d.getMonth() + 1 >= 4 ? d.getFullYear() : d.getFullYear() - 1; };

export default function CalendarDaysCard() {
  const [year, setYear]   = useState(thisYear());
  const [data, setData]   = useState(null);
  const [text, setText]   = useState('');
  const [preview, setPreview] = useState(null);
  const [errors, setErrors]   = useState([]);
  const [busy, setBusy]   = useState(false);
  const [msg, setMsg]     = useState('');
  const [err, setErr]     = useState('');
  // カレンダーから選んで入れる
  const [month, setMonth] = useState(() => { const d = new Date(); return d.getMonth(); });   // 0-11
  const [sel, setSel]     = useState([]);        // 選んだ日 'YYYY-MM-DD'
  const [anchor, setAnchor] = useState(null);    // Shift+クリックの起点
  const [form, setForm]   = useState({ kind: 'holiday', fromPeriod: '5', asWeekday: '1', label: '' });
  const [showPaste, setShowPaste] = useState(false);
  const [showList, setShowList]   = useState(false);
  const [wdScope, setWdScope]     = useState('year');   // 曜日で選ぶ範囲: 'year' | 'month'

  const load = async () => {
    try {
      const r = await fetch('/api/calendar-days?year=' + year, { headers: auth() });
      const d = await r.json();
      if (!d.success) throw new Error(d.message || '読み込めませんでした');
      setData(d);
    } catch (e) { setErr(e.message); }
  };
  useEffect(() => { load(); setSel([]); }, [year]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (dryRun) => {
    setBusy(true); setErr(''); setMsg(''); setErrors([]);
    try {
      const r = await fetch('/api/calendar-days/import', { method: 'POST', headers: auth(), body: JSON.stringify({ text, dryRun }) });
      const d = await r.json();
      if (d.code === 'PARSE_ERROR') { setErrors(d.errors || []); setPreview(null); throw new Error(d.message); }
      if (!r.ok || !d.success) throw new Error(d.message || '登録できませんでした');
      if (dryRun) setPreview(d);
      else { setMsg(d.message); setPreview(null); setText(''); load(); }
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const remove = async (row) => {
    if (!(await confirmDialog(`${row.day} の「${KIND[row.kind] ? KIND[row.kind].label : row.kind}」を消しますか？`))) return;
    try {
      const r = await fetch('/api/calendar-days/' + row.id, { method: 'DELETE', headers: auth() });
      const d = await r.json();
      if (!r.ok || !d.success) throw new Error(d.message || '消せませんでした');
      setMsg(d.message); load();
    } catch (e) { setErr(e.message); }
  };

  const fmt = (day) => { const [y, m, d] = day.split('-').map(Number); return `${m}/${d}（${DAY[new Date(y, m - 1, d).getDay()]}）`; };
  const badge = (k) => {
    const s = KIND[k] || { label: k, color: '#374151', bg: '#f3f4f6' };
    return <span style={{ background: s.bg, color: s.color, borderRadius: 999, padding: '0.05rem 0.5rem', fontSize: '0.78rem', fontWeight: 700 }}>{s.label}</span>;
  };
  const detail = (x) => [
    x.from_period || x.fromPeriod ? `${x.from_period || x.fromPeriod}講時以降` : '',
    (x.as_weekday ?? x.asWeekday) != null ? `${DAY[x.as_weekday ?? x.asWeekday]}曜の授業` : '',
    x.label || '',
  ].filter(Boolean).join(' ');

  // ── カレンダーから選んで入れる ──
  const pad = (n) => String(n).padStart(2, '0');
  const ymdOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const byDay = new Map(((data && data.days) || []).map(x => [x.day, x]));
  const monthYear = month >= 3 ? year : year + 1;        // 年度の4月〜翌3月
  const monthOrder = [3, 4, 5, 6, 7, 8, 9, 10, 11, 0, 1, 2];
  const cells = (() => {
    const first = new Date(monthYear, month, 1);
    const out = [];
    for (let i = 0; i < first.getDay(); i++) out.push(null);
    for (let d = 1; d <= new Date(monthYear, month + 1, 0).getDate(); d++) out.push(new Date(monthYear, month, d));
    return out;
  })();
  const clickDay = (d, e) => {
    const k = ymdOf(d);
    setMsg(''); setErr('');
    if (e.shiftKey && anchor) {
      const [a, b] = [anchor, k].sort();
      const range = [];
      for (let x = new Date(a); ymdOf(x) <= b; x.setDate(x.getDate() + 1)) range.push(ymdOf(x));
      setSel(v => [...new Set([...v, ...range])].sort());
    } else {
      const on = sel.includes(k);
      const next = on ? sel.filter(x => x !== k) : [...sel, k].sort();
      setSel(next);
      // 1日だけ選んで、その日に設定があれば中身をフォームに入れる（直しやすく）
      if (!on && next.length === 1 && byDay.has(k)) {
        const x = byDay.get(k);
        setForm({ kind: x.kind, fromPeriod: String(x.from_period || '5'), asWeekday: String(x.as_weekday ?? '1'), label: x.label || '' });
      } else if (!on && next.length === 1) {
        // 新しく選び始めたら、前に選んだ日の名前を持ち越さない
        setForm(f => ({ ...f, label: '' }));
      }
    }
    setAnchor(k);
  };
  // 曜日でまとめて選ぶ（設定済みの日は外す＝授業実施日や振替を上書きしない）
  const pickWeekday = (w) => {
    setMsg(''); setErr('');
    const from = wdScope === 'year' ? new Date(year, 3, 1) : new Date(monthYear, month, 1);
    const to   = wdScope === 'year' ? new Date(year + 1, 2, 31) : new Date(monthYear, month + 1, 0);
    const picked = [], skipped = [];
    for (let x = new Date(from); x <= to; x.setDate(x.getDate() + 1)) {
      if (x.getDay() !== w) continue;
      const k = ymdOf(x);
      (byDay.has(k) ? skipped : picked).push(k);
    }
    setSel(v => [...new Set([...v, ...picked])].sort());
    setForm(f => ({ ...f, kind: 'holiday', label: w === 0 ? '日曜日' : w === 6 ? '土曜日' : '' }));
    setAnchor(null);
    setMsg(`${DAY[w]}曜を ${picked.length}日選びました` + (skipped.length ? `（設定済みの ${skipped.length}日は外しています）` : '') + '。右で種類を選んで登録してください');
  };
  const saveSelected = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      const r = await fetch('/api/calendar-days/days', { method: 'POST', headers: auth(),
        body: JSON.stringify({ days: sel, kind: form.kind, fromPeriod: form.fromPeriod, asWeekday: form.asWeekday, label: form.label }) });
      const d = await r.json();
      if (!r.ok || !d.success) throw new Error(d.message || '登録できませんでした');
      setMsg(d.message); setSel([]); load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const clearSelected = async () => {
    const targets = sel.filter(k => byDay.has(k));
    if (!(await confirmDialog(`選んだ日のうち ${targets.length}日の設定を消して、ふつうの授業日に戻しますか？`))) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      const r = await fetch('/api/calendar-days/remove', { method: 'POST', headers: auth(), body: JSON.stringify({ days: targets }) });
      const d = await r.json();
      if (!r.ok || !d.success) throw new Error(d.message || '消せませんでした');
      setMsg(d.message); setSel([]); load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const selLabel = sel.length <= 6
    ? sel.map(k => fmt(k)).join('、')
    : `${fmt(sel[0])} 〜 ${fmt(sel[sel.length - 1])} など ${sel.length}日`;

  if (data && data.available === false) {
    return (
      <div className="card">
        <div className="card-header"><h2>授業カレンダー（休業日・振替）</h2></div>
        <p style={{ color: '#b45309' }}>データベースの更新 v49 を当てると使えます。</p>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
        <h2 style={{ margin: 0, flex: 1 }}>授業カレンダー（休業日・振替）</h2>
        <select value={year} onChange={e => setYear(Number(e.target.value))}>
          {[thisYear() - 1, thisYear(), thisYear() + 1].map(y => <option key={y} value={y}>{y}年度</option>)}
        </select>
      </div>
      <p style={{ fontSize: '0.85rem', color: '#4b5563', marginTop: 0 }}>
        休業日などを入れると、「この先の授業」の予定と自動出席に反映されます
        （休業の日は開かない・振替の日は振替先の曜日の授業を開く）。祝日でも授業をする日は入れなくてかまいません。
      </p>
      <Notice kind="error" compact>{err}</Notice>
      <Notice kind="success" toast>{msg}</Notice>

      {/* ── 月のカレンダー ── */}
      <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
        {monthOrder.map(m => (
          <button key={m} type="button" onClick={() => setMonth(m)}
                  className={m === month ? 'btn-primary' : 'btn-secondary'}
                  style={{ padding: '0.15rem 0.55rem', fontSize: '0.82rem' }}>
            {m + 1}月
            {((data && data.days) || []).some(x => Number(x.day.slice(5, 7)) === m + 1) && <span style={{ marginLeft: 2 }}>•</span>}
          </button>
        ))}
      </div>
      {data && data.canEdit && (
        <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.6rem', fontSize: '0.85rem' }}>
          <span style={{ color: '#4b5563' }}>曜日でまとめて選ぶ:</span>
          <select value={wdScope} onChange={e => setWdScope(e.target.value)} style={{ fontSize: '0.82rem' }}>
            <option value="year">{year}年度ぜんぶ</option>
            <option value="month">{month + 1}月だけ</option>
          </select>
          {DAY.map((w, i) => (
            <button key={w} type="button" className="btn-secondary" disabled={busy} onClick={() => pickWeekday(i)}
                    title={`${wdScope === 'year' ? year + '年度' : (month + 1) + '月'}の${w}曜をすべて選ぶ`}
                    style={{ padding: '0.15rem 0.55rem', fontSize: '0.82rem',
                             color: i === 0 ? '#dc2626' : i === 6 ? '#2563eb' : undefined }}>
              {w}曜
            </button>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 420px', minWidth: 300 }}>
          <div style={{ fontWeight: 700, marginBottom: '0.3rem' }}>{monthYear}年{month + 1}月</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3 }}>
            {DAY.map((w, i) => (
              <div key={w} style={{ textAlign: 'center', fontSize: '0.8rem', fontWeight: 700,
                                    color: i === 0 ? '#dc2626' : i === 6 ? '#2563eb' : '#4b5563' }}>{w}</div>
            ))}
            {cells.map((d, i) => {
              if (!d) return <div key={'e' + i} />;
              const k = ymdOf(d);
              const x = byDay.get(k);
              const st = x ? KIND[x.kind] : null;
              const on = sel.includes(k);
              return (
                <button key={k} type="button" onClick={e => data && data.canEdit && clickDay(d, e)}
                        title={x ? `${KIND[x.kind] ? KIND[x.kind].label : x.kind} ${detail(x)}` : (data && data.canEdit ? 'クリックで選ぶ（Shift+クリックで範囲）' : '')}
                        style={{
                          minHeight: 54, textAlign: 'left', verticalAlign: 'top', padding: '0.2rem 0.3rem',
                          borderRadius: 6, cursor: data && data.canEdit ? 'pointer' : 'default',
                          border: on ? '2px solid #2563eb' : '1px solid #e5e7eb',
                          background: on ? '#dbeafe' : st ? st.bg : '#fff',
                          color: d.getDay() === 0 ? '#dc2626' : d.getDay() === 6 ? '#2563eb' : '#111827',
                        }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{d.getDate()}</div>
                  {x && (
                    <div style={{ fontSize: '0.68rem', color: st ? st.color : '#374151', lineHeight: 1.25,
                                  overflow: 'hidden', maxHeight: '2.5em' }}>
                      {st ? st.label : x.kind}
                      {x.kind === 'partial' && ` ${x.from_period}限〜`}
                      {x.kind === 'swap' && ` ${DAY[x.as_weekday]}曜`}
                      {x.label && <div style={{ color: '#6b7280' }}>{x.label}</div>}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginTop: '0.4rem', fontSize: '0.78rem' }}>
            {Object.entries(KIND).map(([k, v]) => (
              <span key={k}><span style={{ display: 'inline-block', width: 10, height: 10, background: v.bg, border: `1px solid ${v.color}`, marginRight: 3 }} />{v.label}</span>
            ))}
          </div>
        </div>

        {/* 選んだ日に付ける */}
        {data && data.canEdit && (
          <div style={{ flex: '1 1 260px', minWidth: 240, background: '#f8fafc', border: '1px solid #e5e7eb',
                        borderRadius: 8, padding: '0.7rem 0.8rem' }}>
            {sel.length === 0 ? (
              <div style={{ fontSize: '0.86rem', color: '#6b7280', lineHeight: 1.8 }}>
                左のカレンダーで日をクリックして選んでください。<br />
                Shift+クリックで期間をまとめて選べます（例: 冬期休業）。<br />
                設定済みの日を1つ選ぶと、中身を直せます。<br />
                上の「日曜」などのボタンで、その曜日をまとめて選べます。
              </div>
            ) : (
              <>
                <div style={{ fontWeight: 700, marginBottom: '0.3rem' }}>選んだ日（{sel.length}日）</div>
                <div style={{ fontSize: '0.84rem', color: '#374151', marginBottom: '0.5rem' }}>{selLabel}</div>
                <div style={{ display: 'grid', gap: '0.3rem', marginBottom: '0.5rem' }}>
                  {[
                    ['holiday', '休業（授業なし）'],
                    ['partial', '一部休講（○講時以降なし）'],
                    ['swap', '振替（別の曜日の授業をする）'],
                    ['class_day', '授業実施日（祝日だが授業あり）'],
                    ['no_auto', '自動開始なし（授業・出席はあり。受付は手で開く）'],
                  ].map(([k, l]) => (
                    <label key={k} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.88rem' }}>
                      <input type="radio" name="calkind" checked={form.kind === k} onChange={() => setForm(f => ({ ...f, kind: k }))} />
                      {l}
                    </label>
                  ))}
                </div>
                {(form.kind === 'partial' || form.kind === 'swap') && (
                  <div style={{ marginBottom: '0.4rem', fontSize: '0.86rem' }}>
                    <select value={form.fromPeriod} onChange={e => setForm(f => ({ ...f, fromPeriod: e.target.value }))}>
                      {form.kind === 'swap' && <option value="">終日</option>}
                      {[1, 2, 3, 4, 5, 6, 7].map(n => <option key={n} value={n}>{n}講時以降</option>)}
                    </select>
                    {form.kind === 'swap' && (
                      <>
                        {' は '}
                        <select value={form.asWeekday} onChange={e => setForm(f => ({ ...f, asWeekday: e.target.value }))}>
                          {DAY.map((w, i) => <option key={i} value={i}>{w}曜</option>)}
                        </select>
                        {' の授業をする'}
                      </>
                    )}
                    {form.kind === 'partial' && ' は授業なし'}
                  </div>
                )}
                <input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                       placeholder="名前（例: 勤労感謝の日・龍谷祭）" style={{ width: '100%', boxSizing: 'border-box', marginBottom: '0.5rem' }} />
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  <button className="btn-primary" disabled={busy} onClick={saveSelected}>{sel.length}日に登録</button>
                  {sel.some(k => byDay.has(k)) && (
                    <button className="btn-danger" disabled={busy} onClick={clearSelected}>設定を消す</button>
                  )}
                  <button className="btn-secondary" disabled={busy} onClick={() => setSel([])}>選択を外す</button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* ── 一覧（畳む） ── */}
      {data && (data.days || []).length > 0 && (
        <div style={{ marginTop: '0.8rem' }}>
          <button type="button" onClick={() => setShowList(v => !v)}
                  style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', padding: 0, fontSize: '0.86rem' }}>
            {showList ? '▼' : '▶'} {year}年度の一覧（{data.days.length}日）
          </button>
          {showList && (
            <div className="table-wrap" style={{ maxHeight: 320, overflowY: 'auto', marginTop: '0.4rem' }}>
              <table className="data-table">
                <thead><tr><th>日付</th><th>種類</th><th>内容</th><th></th></tr></thead>
                <tbody>{data.days.map(x => (
                  <tr key={x.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmt(x.day)}</td>
                    <td>{badge(x.kind)}</td>
                    <td>{detail(x)}</td>
                    <td>{data.canEdit && <button className="btn-secondary" style={{ padding: '0.1rem 0.5rem', fontSize: '0.78rem' }} onClick={() => remove(x)}>消す</button>}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── まとめて貼り付け（畳む） ── */}
      {data && data.canEdit && (
        <div style={{ marginTop: '0.6rem' }}>
          <button type="button" onClick={() => setShowPaste(v => !v)}
                  style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', padding: 0, fontSize: '0.86rem' }}>
            {showPaste ? '▼' : '▶'} まとめて貼り付け（学年暦から一気に入れるとき）
          </button>
          {showPaste && (
            <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.7rem 0.8rem', marginTop: '0.4rem' }}>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.35rem', lineHeight: 1.7 }}>
                1行に1日（または「〜」で期間）。種類は 休業・一部休講・振替・授業実施・自動開始なし。同じ日は上書きします。<br />
                <code>2026-11-23 休業 勤労感謝の日</code>　<code>2026-12-29〜2027-01-05 休業 冬期休業</code><br />
                <code>2026-05-13 一部休講 5講時以降 降誕会</code>　<code>2026-05-30 振替 5講時以降 水曜 水曜日7回目分</code>
              </div>
              <textarea value={text} onChange={e => { setText(e.target.value); setPreview(null); setErrors([]); }}
                        rows={8} style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'monospace' }} />
              {errors.length > 0 && (
                <div style={{ color: '#b91c1c', fontSize: '0.85rem', margin: '0.3rem 0' }}>
                  {errors.map(e => <div key={e.line}>{e.line}行目「{e.text}」: {e.message}</div>)}
                </div>
              )}
              {preview && (
                <div style={{ fontSize: '0.85rem', margin: '0.4rem 0', color: '#1e3a8a' }}>
                  {preview.count}日ぶんを読み取りました（新しく {preview.added}日・書き換え {preview.changed.length}日）。
                  {preview.changed.length > 0 && (
                    <div style={{ color: '#b45309' }}>
                      書き換わる日: {preview.changed.map(c => `${c.before} → ${c.after}`).join(' / ')}
                    </div>
                  )}
                </div>
              )}
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.4rem' }}>
                <button className="btn-secondary" disabled={busy || !text.trim()} onClick={() => send(true)}>下見</button>
                <button className="btn-primary" disabled={busy || !preview} onClick={() => send(false)}>
                  {preview ? `${preview.count}日ぶんを登録` : '登録（先に下見）'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
